import { Inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { ExternalChatParamsI } from '@components/common/external-chat-loader/external-chat-loader.component';
import { ConfirmDialogComponent } from '@components/dialogs/confirm-dialog/confirm-dialog.component';
import { environment } from '@environments/environment';
import { ChatSocketService } from '@events/chat/chat-socket.service';
import { GroupSocketService } from '@events/chat/group-socket.service';
import { LogoffReminderService } from '@helpers/logoff-reminder.service';
import { RubeusIntegrationHelperService } from '@helpers/rubeus-integration-helper.service';
import { TourHelperService } from '@helpers/tour-helper.service';
import { UserHelperService } from '@helpers/user-helper.service';
import { UserAttributesI } from '@interfaces/user.interface';
import { ChatStoreService } from '@modules/chat/store/chat-store.service';
import { GroupStoreService } from '@modules/chat/store/group-store.service';
import { CustomersService } from '@modules/customers/customers.service';
import { GlobalStoreService } from '@modules/global/store/global-store.service';
import { ReportPauseService } from '@modules/reports/report-pause/report-pause.service';
import { UserPreferencesService } from '@modules/users/user.preferences.service';
import { AuthenticationService } from '@modules/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { ExtraValidationsService } from '@services/acl/extra-validations.service';
import { SipConnectionManagerService } from '@services/common/sip-connection-manager.service';
import { ChatListenersService } from '@services/listeners/chat-listeners.service';
import { ColorThemeService } from '@services/theme/color-theme.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as Sentry from '@sentry/angular-ivy';
import { TabulationService } from '@services/tabulation/tabulation.service';
import { ParametersHttpService } from '@services/parameters/parameters-http.service';
import { TemplateLimitService } from '@services/template-limit/template-limit.service';
import { CallHistoryStatusE, CallReasonDisconnectE } from '@enums/call-history.enum';
import { ParameterTypeE } from '@enums/parameter-type.enum';
import { StatusE } from '@interfaces/flow.interface';
import { FileExporterSocketService } from '@events/file/file-export-socket.service';
import { CallHistoryManagerService } from '@components/chat/chat-dialer/call-history-manager.service';
import { CallHistoryI } from '@interfaces/call-history.interface';
import { SessionStorageService } from '@services/common/session-storage.service';
import { TaskSocketService } from '@services/task/task-socket.service';
import { TeamsService } from '@modules/teams/teams.service';
import { ChannelsHttpService } from '@services/channels/channels-http.service';
import { TemplateHsmService } from '@modules/template-hsm/template-hsm.service';
import { ContactSocketService } from '@events/contact/contact-socket.service';
import { CustomerFeatureFlagsService } from '@services/customer-feature-flags/customer-feature-flags.service';
import { EmailSendingHealthNotificationService } from '@services/email-sending-health/email-sending-health-notification.service';
import { TabulationDialogService } from '@services/tabulation/tabulation-dialog.service';

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  // initializeSocket runs again on every login, so the reconnect wiring is single-flight.
  private reconnectSubscription?: Subscription;

  constructor(
    private globalStateService: GlobalStoreService,
    private userPreferencesService: UserPreferencesService,
    private fileExporterSocketService: FileExporterSocketService,
    private translateService: TranslateService,
    private router: Router,
    private tourHelperService: TourHelperService,
    private colorThemeService: ColorThemeService,
    private rubeusIntegrationService: RubeusIntegrationHelperService,
    @Inject(ChatSocketService) private chatSocketService: ChatSocketService,
    @Inject(GroupSocketService) private groupSocketService: GroupSocketService,
    private chatStoreService: ChatStoreService,
    private groupStoreService: GroupStoreService,
    private userHelperService: UserHelperService,
    private reportPauseService: ReportPauseService,
    @Inject(ChatListenersService) private chatListenersService: ChatListenersService,
    private logoffReminder: LogoffReminderService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private extraValidatoresService: ExtraValidationsService,
    private customerService: CustomersService,
    private globalStoreService: GlobalStoreService,
    private sipConnectionManager: SipConnectionManagerService,
    private tabulationsService: TabulationService,
    private parametersService: ParametersHttpService,
    private templateLimitService: TemplateLimitService,
    private readonly callHistoryManager: CallHistoryManagerService,
    private sessionStorageService: SessionStorageService,
    private taskSocketService: TaskSocketService,
    private teamsService: TeamsService,
    private channelsHttpService: ChannelsHttpService,
    private templateHsmService: TemplateHsmService,
    private contactSocketService: ContactSocketService,
    private customerFeatureFlags: CustomerFeatureFlagsService,
    private readonly tabulationDialogService: TabulationDialogService,
    private readonly emailSendingHealthNotifications: EmailSendingHealthNotificationService,
    private injector: Injector
  ) {}

  public async initialize(options?: { fromLogin?: boolean }) {
    this.sessionStorageService.generateSessionId();
    this.initializeQueryParams();
    await this.recoverExpiredSessionOnBootstrap(options);
    await this.initializeLanguage();
    this.initializeTourListener();
    this.initializeTheme();
    await this.checkRubeusIntegration();
    await this.checkForLinkChatOpen();
    await this.initializeExternalValidators();
    await this.sipConnectionManager.listenEvents();
    await this.initializeSocket();
    this.initializeSentryTags();
    this.logoffReminder.init();
    this.isRefreshed();
    this.checkForUnfinishedCalls();
    this.initPendingCallHistoryFlush();
    this.tabulationsService.initOnce();
    await this.parametersService.initOnce();
    this.checkPendingTabulation();
    this.taskSocketService.init();
    this.teamsService.initOnce();
    this.channelsHttpService.initOnce();
    this.templateHsmService.initOnce();
    // Loads the sending-health alerts that arrived while nobody was logged in:
    // the socket event alone would only ever reach whoever was already online.
    void this.emailSendingHealthNotifications.initOnce();
    if (options?.fromLogin) {
      this.finishPauses();
    }
  }

  // Cold-start session recovery: the AuthGuard only checks token PRESENCE and the
  // token refresh is reactive (fires on a 401), which can bail to a silent local
  // logout when the stored user data is momentarily unavailable. Here we rotate an
  // already-expired idToken proactively, before the first authenticated request:
  // the email is read from the still-decodable idToken and the refresh token from
  // its cookie, so recovery survives a lost localStorage. Best-effort and time-boxed
  // so it never blocks bootstrap; on any failure the existing reactive flow runs.
  private async recoverExpiredSessionOnBootstrap(options?: { fromLogin?: boolean }): Promise<void> {
    if (options?.fromLogin) {
      return;
    }
    try {
      const idToken = this.userHelperService.getIdToken();
      const refreshToken = this.userHelperService.getRefreshToken();
      if (!idToken || !refreshToken) {
        return;
      }
      const payload = this.decodeJwtPayload(idToken);
      const exp = payload?.exp;
      const email = payload?.email;
      if (!email || !exp || exp * 1000 > Date.now()) {
        return;
      }
      const userLoginData = this.userHelperService.getUserLoginData();
      const authenticationService = this.injector.get(AuthenticationService);
      const recover = (async () => {
        const result = await firstValueFrom(
          authenticationService.refreshToken({ email, refreshToken, userLoginData, token: idToken })
        );
        await this.userHelperService.saveDataToken(result);
      })();
      await Promise.race([recover, new Promise<void>((resolve) => setTimeout(resolve, 8000))]);
    } catch {
      // fall back to the existing reactive refresh; never block startup
    }
  }

  private decodeJwtPayload(token: string): { exp?: number; email?: string } | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(decodeURIComponent(escape(atob(base64))));
    } catch {
      return null;
    }
  }

  private isRefreshed() {
    this.router.events
      .pipe(
        filter((rs): rs is NavigationEnd => rs instanceof NavigationEnd),
        filter(() => this.userHelperService.getUserAttributes()?.['custom:isPhone'] === 'true')
      )
      .subscribe((event) => {
        if (event.id === 1 && event.url === event.urlAfterRedirects) {
          if (!environment.IS_DEVELOPMENT) {
            this.dialog.open(ConfirmDialogComponent, {
              panelClass: 'custom-dialog-v2',
              width: '300px',
              hasBackdrop: true,
              data: {
                message: this.translate.instant('REFRESHED_PAGE_TEXT'),
                confirmButtonText: 'OK',
              },
            });
          }
        }
      });
  }

  private initializeQueryParams() {
    const urlParams = new URLSearchParams(location.search);
    const paramsObj = Array.from(urlParams.keys()).reduce((acc, val) => ({ ...acc, [val]: urlParams.get(val) }), {});
    this.globalStateService.setQueryParams(paramsObj);
  }

  public async initializeLanguage() {
    const preferences = await this.userPreferencesService.getPreferences();
    const navigatorLanguage = navigator.language;

    setTimeout(() => {
      this.translateService.use(preferences?.language ?? navigatorLanguage);
    }, 1000);
  }

  private initializeTourListener() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        setTimeout(() => this.tourHelperService.startTourForCurrentRoute(), 200);
      }
    });
  }

  private async checkRubeusIntegration() {
    await this.rubeusIntegrationService.checkForIntegration();
  }

  private async initializeTheme() {
    const preferences = await this.userPreferencesService.getPreferences();
    if (preferences && preferences.theme) {
      this.colorThemeService.setTheme(preferences.theme);
    }
  }

  private async checkForLinkChatOpen() {
    const params = await this.globalStateService.getQueryParams();
    const { chatId } = params || {};
    if (chatId) {
      this.router.navigate(['/external-chat'], {
        state: {
          chatId: chatId,
        } as ExternalChatParamsI,
      });
    }
  }

  public async stopSocket() {
    this.chatSocketService?.userLogout();
    this.groupSocketService?.userLogout();
  }

  public async initializeSocket() {
    if (!this.isUserLoggedIn()) {
      this.stopSocket();
      return;
    }

    this.chatSocketService.init({
      connectionCallback: async () => {
        const canRefreshChat = await firstValueFrom(this.chatSocketService.canRefreshChat$);
        const canRefreshGroup = await firstValueFrom(this.groupSocketService.canRefreshGroup$);
        if (canRefreshChat) {
          this.chatStoreService.refreshChat();
          this.chatSocketService.setRefreshChat(false);
        } else {
          // Rooms are only joined from the list the HTTP fetch returned (chat-list-body ->
          // listAllChatsRooms), so a chat assigned between that fetch and the join is never
          // announced, and chatLoaded[type] latches the list against a refetch — the chat stays
          // invisible until the user toggles list tabs. A connect with no pending disconnect
          // flag still re-reads the current type once (single page, byReconnection = keeps
          // queues/notifications) to close that window.
          this.chatStoreService.refreshChat(true);
        }
        if (canRefreshGroup) {
          this.groupStoreService.refreshGroup();
          this.groupSocketService.setRefreshGroup(false);
        }
      },
    });

    // socket.io fires its own 'reconnect' after the transport drops; the subject existed but
    // had no subscriber, so a reconnection that did not go through onDisconnect left the list
    // stale forever.
    this.reconnectSubscription?.unsubscribe();
    this.reconnectSubscription = this.chatSocketService.reconnected$.subscribe(() =>
      this.chatStoreService.refreshChat(true)
    );
    this.groupSocketService.init();
    this.fileExporterSocketService.init();
    this.contactSocketService.init();

    this.chatSocketService.onDisconnect(() => {
      this.chatSocketService.setRefreshChat(true);
    });

    this.chatSocketService.onDisconnectLoginSocket(() => {
      this.chatSocketService.setRefreshLogin(true);
    });

    this.groupSocketService.onDisconnect(() => {
      this.groupSocketService.setRefreshGroup(true);
    });

    this.chatSocketService.onConnectLoginSocket(async () => {
      const setRefreshLogin = await firstValueFrom(this.chatSocketService.canRefreshLogin$);
      if (setRefreshLogin) {
        this.chatSocketService.setRefreshLogin(false);
      }

      this.chatSocketService.emitUserLoginToChatNamespace();
    });

    this.chatListenersService.initialize();
  }

  private isUserLoggedIn() {
    return !!this.userHelperService.getUserAttributes();
  }

  public initializeSentryTags() {
    //adiciona ao sentry informações do usuario logado
    const userAttributes = this.userHelperService.getUserAttributes();
    userAttributes;
    if (userAttributes) {
      const tags = ['custom:customer', 'custom:userId', 'email', 'name'];
      tags.forEach((tag) => {
        const tagValue = userAttributes[tag];
        if (tagValue) {
          Sentry.setTag(tag, tagValue);
        }
      });
    }
  }


  private pendingFlushInterval?: ReturnType<typeof setInterval>;
  private pendingFlushBound = false;

  private initPendingCallHistoryFlush() {
    void this.callHistoryManager.flushPending();

    if (!this.pendingFlushBound) {
      this.pendingFlushBound = true;
      window.addEventListener('online', () => {
        void this.callHistoryManager.flushPending();
      });
    }

    if (!this.pendingFlushInterval) {
      this.pendingFlushInterval = setInterval(() => {
        void this.callHistoryManager.flushPending();
      }, 60000);
    }
  }

  private async checkForUnfinishedCalls() {
    try {
      const unfinishedCall = this.callHistoryManager.getTempLocalStorage();

      if (unfinishedCall) {
        this.callHistoryManager.setCurrentCall(unfinishedCall as CallHistoryI);
        const translate = this.translateService.instant('LOST_CONNECTION_AFTER_REFRESH');

        unfinishedCall.status = CallHistoryStatusE.DISCONNECTED;
        unfinishedCall.reasonDisconnect = CallReasonDisconnectE.PAGE_REFRESH;
        unfinishedCall.commentTabulation = `No tabulation. Call lost connection because ${translate}.`;

        const callHistory = await this.callHistoryManager.createOrUpdate(unfinishedCall);

        if (callHistory?._id) {
          this.callHistoryManager.clear();
        }
      }
    } catch (error) {
      console.error('Error checking for unfinished calls:', error);
    }
  }

  /**
   * On load, if the agent has a call awaiting mandatory tabulation (persisted in the backend),
   * reopens the tabulation modal so a prior page refresh doesn't lose the tabulation.
   */
  private async checkPendingTabulation(): Promise<void> {
    // runs inside the pre-login APP_INITIALIZER too; skip until authenticated so we don't hit call-history without a session (401)
    if (!this.userHelperService.getUserAttributes()?.['custom:userId']) {
      return;
    }

    try {
      const pending = await this.callHistoryManager.getPendingTabulation();
      if (!pending?.callHistoryId) {
        return;
      }

      const infoTabulation = await this.tabulationDialogService.openCallTabulation(
        {
          contactName: pending.contactName,
          number: pending.number,
          protocolNumber: pending.protocolNumber,
          duration: pending.duration,
        },
        true
      );
      if (!infoTabulation?.tabulationIds?.length) {
        return;
      }

      await this.callHistoryManager.concludeTabulationById(pending.callHistoryId, infoTabulation);
      await this.endPendingTabulationPause();
    } catch (error) {
      console.error('Error checking pending tabulation:', error);
    }
  }

  private async endPendingTabulationPause(): Promise<void> {
    const loggedInUser = this.userHelperService.getUserAttributes();
    if (loggedInUser) {
      const userId = loggedInUser['custom:userId'];
      const customerId = loggedInUser['custom:customer'];
      if (userId && customerId) {
        await this.reportPauseService.endPausePabxByUserId(userId, customerId);
      }
    }
  }

  private async finishPauses() {
    const loggedInUser = this.userHelperService.getUserAttributes();
    if (loggedInUser) {
      const userId = loggedInUser['custom:userId'];
      const customer = loggedInUser['custom:customer'];

      try {
        await firstValueFrom(this.reportPauseService.endPauseByUserId(userId, customer));
      } catch (error) {
        console.error('Error finishing pause:', error);
      }
    }
  }


  private async initializeExternalValidators() {
    const userAttrs = this.userHelperService.getUserAttributes();
    const currentsettings = await firstValueFrom(this.globalStoreService.customerSettings$);
    if (userAttrs && !currentsettings) {
      if (userAttrs['custom:customer']) {
        let settings = await firstValueFrom(this.customerService.getcustomerSettingsById(userAttrs['custom:customer']));
        const useTemplateLimitReport = await firstValueFrom(this.templateLimitService.checkIfLoggedCustomerHasLimits());
        const useAwayReport = await this.checkAwayReportEnabled();
        const useBlockReport = await this.checkBlockReportEnabled();

        if (settings) {
          if (useTemplateLimitReport) {
            settings = { ...settings, useTemplateLimitReport: true };
          }

          settings = { ...settings, useAwayReport, useBlockReport, customerId: userAttrs['custom:customer'] };

          this.globalStoreService.setcustomerSettings(settings);
          this.customerFeatureFlags.setSettings(settings);

          if (settings.snipersellOrgId) {
            this.globalStoreService.setSnipersellOrgId(settings.snipersellOrgId);
          }
        }
      }
    }
    const validators = await this.extraValidatoresService.getAllValidators();
    this.globalStoreService.setGlobalPersmissionsOverrides(validators);
  }

  private async checkAwayReportEnabled(): Promise<boolean> {
    try {
      const param = await firstValueFrom(this.parametersService.findLastByType(ParameterTypeE.AWAY));
      return param?.status === StatusE.ACTIVE;
    } catch (error) {
      return false;
    }
  }

  private async checkBlockReportEnabled(): Promise<boolean> {
    try {
      const param = await firstValueFrom(this.parametersService.findLastByType(ParameterTypeE.BLOCK));
      return param?.status === StatusE.ACTIVE;
    } catch (error) {
      return false;
    }
  }
}
